import { useCallback, useEffect, useRef, useState } from 'react'
import { UseFormWatch } from 'react-hook-form'
import { DRAFT_KEY } from '../constants'
import { VendorOnboardingForm } from '../interfaces'

interface StoredDraft {
    savedAt: number
    values: Omit<VendorOnboardingForm, 'gstCertificate' | 'website'>
}

export const readDraft = (): StoredDraft | null => {
    try {
        const raw = window.localStorage.getItem(DRAFT_KEY)
        if (!raw) return null
        const parsed = JSON.parse(raw) as StoredDraft
        return parsed?.values ? parsed : null
    } catch {
        return null
    }
}

export const clearDraft = () => {
    try {
        window.localStorage.removeItem(DRAFT_KEY)
    } catch {
        /* storage unavailable — nothing to clean up */
    }
}

export const relativeTime = (timestamp: number): string => {
    const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000))
    if (seconds < 60) return 'just now'
    const minutes = Math.round(seconds / 60)
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
    const days = Math.round(hours / 24)
    return `${days} day${days === 1 ? '' : 's'} ago`
}

/**
 * Debounced autosave of everything except the certificate and the honeypot.
 *
 * A File cannot be serialised to localStorage, so on resume every text field comes
 * back but the attachment slot is empty — the UI says so explicitly rather than
 * letting someone submit thinking the file is still attached.
 */
const useDraftPersistence = (
    watch: UseFormWatch<VendorOnboardingForm>,
    enabled: boolean
) => {
    const [savedAt, setSavedAt] = useState<number | null>(null)
    const timerRef = useRef<number | undefined>(undefined)
    const dirtyRef = useRef(false)

    useEffect(() => {
        if (!enabled) return undefined

        const subscription = watch((values) => {
            dirtyRef.current = true
            window.clearTimeout(timerRef.current)
            timerRef.current = window.setTimeout(() => {
                const { gstCertificate, website, ...rest } =
                    values as VendorOnboardingForm
                try {
                    const stamp = Date.now()
                    window.localStorage.setItem(
                        DRAFT_KEY,
                        JSON.stringify({ savedAt: stamp, values: rest })
                    )
                    setSavedAt(stamp)
                } catch {
                    /* quota or private mode — autosave is best-effort */
                }
            }, 800)
        })

        return () => {
            subscription.unsubscribe()
            window.clearTimeout(timerRef.current)
        }
    }, [watch, enabled])

    useEffect(() => {
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!dirtyRef.current || !enabled) return
            event.preventDefault()
            event.returnValue = ''
        }
        window.addEventListener('beforeunload', onBeforeUnload)
        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [enabled])

    const markClean = useCallback(() => {
        dirtyRef.current = false
    }, [])

    return { savedAt, markClean }
}

export default useDraftPersistence
